/**
 * Threads, as the desktop app reads them out of one conversation's log.
 *
 * Pure functions over a log and the set of ids it has ever held, so both
 * stores share them and a test can read them without a socket. The rules are
 * `relaydatasource.cpp`'s `messages()`, `threadReplies()` and
 * `summariseThread()`:
 *
 * **A reply belongs to its thread, unless its root is not held.** A thread can
 * begin below `joined_seq` or under an archived range, and hiding a reply in a
 * thread nobody on this device can open would lose it outright. So an orphan
 * is drawn in the transcript, and leaves it when the root arrives.
 *
 * **`held` includes deleted ids.** A deleted message's id stays in the dedup
 * set so a later pull cannot bring it back, and that same set is what decides
 * orphanhood — so the replies to a deleted root are not promoted. They stay
 * held and are drawn nowhere, which is the desktop's behaviour.
 *
 * **Threads are one level deep.** The pane shows the replies that name its
 * root; a reply naming a reply is held and not drawn, as on the desktop.
 */

import type { Message, ThreadSummary, ThreadView } from './types';

/** How many repliers the row under a root draws. */
const FACES = 3;

const NO_REPLIES: Message[] = [];

/** Replies by the root they name, each list in log order. */
function repliesByRoot(log: Message[]): Map<string, Message[]> {
  const out = new Map<string, Message[]>();
  for (const message of log) {
    if (!message.replyTo) continue;
    const list = out.get(message.replyTo);
    if (list) list.push(message);
    else out.set(message.replyTo, [message]);
  }
  return out;
}

function summaryOf(replies: Message[]): ThreadSummary {
  const participants: string[] = [];
  let lastAt = 0;
  for (const reply of replies) {
    if (reply.at > lastAt) lastAt = reply.at;
    if (participants.length < FACES && !participants.includes(reply.senderId)) {
      participants.push(reply.senderId);
    }
  }
  return { replyCount: replies.length, participants, lastAt };
}

/**
 * The transcript lane: every message but the replies whose root is held, and a
 * summary on each root that has replies.
 *
 * A root gets a new object carrying its summary; everything else is the log's
 * own object, so a row whose message did not change keeps its identity.
 */
export function laneOf(log: Message[], held: ReadonlySet<string>): Message[] {
  const threads = repliesByRoot(log);
  const out: Message[] = [];
  for (const message of log) {
    if (message.replyTo && held.has(message.replyTo)) continue;
    const replies = message.replyTo ? undefined : threads.get(message.id);
    out.push(replies ? { ...message, thread: summaryOf(replies) } : message);
  }
  return out;
}

/** The root, its replies oldest first, and whether the root was deleted. */
export function threadOf(log: Message[], held: ReadonlySet<string>, rootId: string): ThreadView {
  const root = log.find((m) => m.id === rootId);
  const replies = log.filter((m) => m.replyTo === rootId);
  return {
    ...(root ? { root } : {}),
    replies: replies.length > 0 ? replies : NO_REPLIES,
    deleted: !root && held.has(rootId)
  };
}

/**
 * The message a conversation's list row previews: the last one in the
 * transcript.
 *
 * A thread reply never replaces it. The row still moves and still counts the
 * reply as unread, so you know the conversation did; the line under the title
 * keeps showing the transcript rather than a fragment whose context is
 * invisible from the list. An orphan is a reply too — the desktop's test is
 * `rt`, not where the reply is drawn.
 */
export function previewOf(log: Message[]): Message | undefined {
  for (let i = log.length - 1; i >= 0; i--) {
    const message = log[i]!;
    if (!message.replyTo) return message;
  }
  return undefined;
}

/**
 * What the thread pane does with what it is given.
 *
 * - `open`: the root is here; draw it and its replies.
 * - `closed`: its author deleted the root. The desktop closes the pane; the
 *   address loses its `?thread=` so a reload does not bring it back.
 * - `loading`: not held, and there is history below what has loaded. A reload
 *   restores a thread through its address, and an old root is not in the tail
 *   the store pulls first.
 * - `missing`: not held, and nothing is left below. A root under `joined_seq`,
 *   or an id that was never a message here.
 */
export type ThreadPaneState = 'open' | 'closed' | 'loading' | 'missing';

export function threadPaneState(view: ThreadView, hasOlder: boolean): ThreadPaneState {
  if (view.root) return 'open';
  if (view.deleted) return 'closed';
  return hasOlder ? 'loading' : 'missing';
}
